import { useMemo, useState } from "react";
import { withNamespaces } from "react-i18next";
import Select from "react-select";
import { toast } from "react-toastify";
import {
    Button,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Spinner,
} from "reactstrap";

import useOrganizationFunctionMutations from "../../../../hooks/useOrganizationFunctionMutations";
import useAssignableUsers from "../../hooks/useAssignableUsers";
import AssignedUserCard from "../assigned-user-card";

const DpoAssignment = ({
    t,
    organizationFunction,
    pendingUser,
    onPendingUserChange,
}) => {
    const assignedUser = Array.isArray(organizationFunction?.assigned_users)
        ? organizationFunction.assigned_users[0]
        : null;

    const [isChangeOpen, setIsChangeOpen] = useState(false);
    const [isRemoveOpen, setIsRemoveOpen] = useState(false);
    const [replacementUser, setReplacementUser] = useState(null);

    const { options, isLoading: isUsersLoading } = useAssignableUsers(t);
    const { replaceDpoMutation, unassignMutation } =
        useOrganizationFunctionMutations({
            functionKey: organizationFunction?.key,
            t,
        });

    const selectableOptions = useMemo(() => {
        if (!assignedUser?.id) {
            return options;
        }

        return options.filter((option) => option.value !== assignedUser.id);
    }, [assignedUser?.id, options]);

    const closeChangeModal = () => {
        setIsChangeOpen(false);
        setReplacementUser(null);
    };

    const handleReplace = () => {
        if (!replacementUser?.value) {
            return;
        }

        replaceDpoMutation.mutate(replacementUser.value, {
            onSuccess: () => {
                toast(t("Data was saved successfully"), {
                    type: "success",
                });
                closeChangeModal();
            },
        });
    };

    const handleRemove = () => {
        if (!assignedUser?.id) {
            return;
        }

        unassignMutation.mutate(assignedUser.id, {
            onSuccess: () => {
                setIsRemoveOpen(false);
            },
        });
    };

    if (assignedUser) {
        return (
            <>
                <AssignedUserCard
                    user={assignedUser}
                    actionsPlacement="below"
                    actions={
                        <>
                            <Button
                                color="primary"
                                outline
                                type="button"
                                onClick={() => setIsChangeOpen(true)}
                            >
                                {t("Change User")}
                            </Button>
                            <Button
                                color="danger"
                                outline
                                type="button"
                                onClick={() => setIsRemoveOpen(true)}
                            >
                                {t("Remove Assignment")}
                            </Button>
                        </>
                    }
                />

                <Modal isOpen={isChangeOpen} backdrop="static">
                    <ModalHeader toggle={closeChangeModal}>
                        {t("Change User")}
                    </ModalHeader>
                    <ModalBody>
                        <p>{t("Are you sure you want to change the assigned user?")}</p>
                        <Select
                            classNamePrefix="select2-selection"
                            isDisabled={
                                isUsersLoading || replaceDpoMutation.isLoading
                            }
                            isLoading={isUsersLoading}
                            menuPortalTarget={document.body}
                            onChange={setReplacementUser}
                            options={selectableOptions}
                            placeholder={t("Search and select a user...")}
                            styles={{
                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                            value={replacementUser}
                        />
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            color="secondary"
                            type="button"
                            onClick={closeChangeModal}
                        >
                            {t("Cancel")}
                        </Button>
                        <Button
                            color="primary"
                            type="button"
                            disabled={
                                !replacementUser?.value ||
                                replaceDpoMutation.isLoading
                            }
                            onClick={handleReplace}
                        >
                            {replaceDpoMutation.isLoading ? (
                                <Spinner className="me-2" size="sm" />
                            ) : null}
                            {t("Confirm")}
                        </Button>
                    </ModalFooter>
                </Modal>

                <Modal isOpen={isRemoveOpen} backdrop="static">
                    <ModalHeader toggle={() => setIsRemoveOpen(false)}>
                        {t("Remove Assignment")}
                    </ModalHeader>
                    <ModalBody>
                        {t("Are you sure you want to remove this assignment?")}
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            color="secondary"
                            type="button"
                            onClick={() => setIsRemoveOpen(false)}
                        >
                            {t("Cancel")}
                        </Button>
                        <Button
                            color="danger"
                            type="button"
                            disabled={unassignMutation.isLoading}
                            onClick={handleRemove}
                        >
                            {unassignMutation.isLoading ? (
                                <Spinner className="me-2" size="sm" />
                            ) : null}
                            {t("Remove Assignment")}
                        </Button>
                    </ModalFooter>
                </Modal>
            </>
        );
    }

    return (
        <div>
            <Label className="form-label" htmlFor="dpo-select-user">
                {`${t("Select user")}:`}
            </Label>
            <Select
                inputId="dpo-select-user"
                classNamePrefix="select2-selection"
                isClearable
                isDisabled={isUsersLoading}
                isLoading={isUsersLoading}
                menuPortalTarget={document.body}
                onChange={onPendingUserChange}
                options={options}
                placeholder={t("Search and select a user...")}
                styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
                value={pendingUser}
            />
            <div className="text-center mt-4">
                <strong className="d-block text-muted">
                    {t("No user assigned")}
                </strong>
                <small className="d-block text-muted mt-1">
                    {t(
                        "Assign a user to the DPO function to make them available for DPO-related approval requests."
                    )}
                </small>
            </div>
        </div>
    );
};

export default withNamespaces()(DpoAssignment);
